/**
 * @file Cross-cutting helpers used by the Firebase service modules: accent-insensitive
 * string handling for client-side search, Firebase Storage object deletion, and a basic
 * mobile-UA check. No store state \u2014 pure functions plus one Storage side-effect.
 */

import { getStorage, ref, deleteObject } from 'firebase/storage'

/**
 * Strips diacritics so search input matches French (and other accented) content
 * without forcing the user to type the accent. Uses NFD normalisation + removal of
 * combining marks; safe to call on any string.
 */
export function removeAccents(input: string): string {
  return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/** Lowercases, trims, and strips accents \u2014 the canonical form used for client-side comparison in search filters. */
export function standardizeString(input: string): string {
  return removeAccents(input.toLowerCase().trim())
}

/**
 * Deletes a file from Firebase Storage by its download URL. Use when removing the
 * Firestore document that owns an upload so orphaned blobs do not accumulate
 * (avatar versions, profile photos, pack images).
 */
export async function deleteImageFromBucket(imageURL: string) {
  const storage = getStorage()
  const imageRef = ref(storage, imageURL)
  await deleteObject(imageRef)
}

/** Coarse user-agent check used to switch between mobile and desktop layouts where CSS media queries are insufficient. */
export function isMobileDevice(): boolean {
  return /Mobi|Android/i.test(navigator.userAgent)
}
